import * as React from 'react';

type Focusable = { focus: () => void };

export type DatasheetNavigationContextValue = {
  registerField: (id: string, el: Focusable | null) => void;
  focusNext: (currentId: string) => void;
  focusPrev: (currentId: string) => void;
};

const noop = () => {};

export const DatasheetNavigationContext = React.createContext<DatasheetNavigationContextValue>({
  registerField: noop,
  focusNext: noop,
  focusPrev: noop,
});

// Used once in the Datasheet parent component to create the registry value
export const useDatasheetNavigation = (orderedIds: string[]): DatasheetNavigationContextValue => {
  const fieldRefs = React.useRef<Map<string, Focusable>>(new Map());
  const orderedIdsRef = React.useRef(orderedIds);

  React.useLayoutEffect(() => {
    orderedIdsRef.current = orderedIds;
  }, [orderedIds]);

  const registerField = React.useCallback((id: string, el: Focusable | null) => {
    if (el) {
      fieldRefs.current.set(id, el);
    } else {
      fieldRefs.current.delete(id);
    }
  }, []);

  const focusNext = React.useCallback((currentId: string) => {
    const ids = orderedIdsRef.current;
    const idx = ids.indexOf(currentId);
    if (idx === -1) return;
    for (let i = idx + 1; i < ids.length; i++) {
      const el = fieldRefs.current.get(ids[i]!);
      if (el) {
        el.focus();
        return;
      }
    }
  }, []);

  const focusPrev = React.useCallback((currentId: string) => {
    const ids = orderedIdsRef.current;
    const idx = ids.indexOf(currentId);
    if (idx === -1) return;
    for (let i = idx - 1; i >= 0; i--) {
      const el = fieldRefs.current.get(ids[i]!);
      if (el) {
        el.focus();
        return;
      }
    }
  }, []);
  return { registerField, focusNext, focusPrev };
};

// Consumed by individual field components
export const useDatasheetNavigationContext = (): DatasheetNavigationContextValue =>
  React.useContext(DatasheetNavigationContext);
